import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState } from 'react';
import { useSession } from '../hooks/useSession';
import { fetchWithAuth } from '../lib/accessToken';

/**
 * Pricing prediction card — Feature 5 tenant-facing UI.
 *
 * Renders an "AuctiSync AI" hammer band + sell-through % + stars-rated
 * confidence + rationale for the lot. Cache-first read against the IE
 * Worker; tenant can click Refresh to force a fresh prediction.
 *
 * Privacy: this component intentionally does NOT show the model name,
 * prompt template version, or token counts. Per Jim's standing rule
 * ("look like AuctiSync, not Anthropic"), tenants see "AuctiSync AI"
 * branding only. Provenance lives in the audit log + DB row, surfaced
 * in the super-admin omniscient view.
 */

interface ConfidenceBand {
  hammerLowCents: number;
  hammerMidCents: number;
  hammerHighCents: number;
  sellThroughProbability: number;
  modelConfidence: number;
}

interface ApiPrediction {
  source: 'cached' | 'computed' | 'fallback';
  band: ConfidenceBand;
  rationale: string;
  generatedAt: string;
  cacheTtlExpiresAt: string;
  provenanceCompLotIds: string[];
}

interface ApiError {
  error: string;
  detail: string;
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'refreshing'; prior: ApiPrediction }
  | { kind: 'ok'; data: ApiPrediction }
  | { kind: 'error'; status: number; message: string };

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

function formatCents(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  return `$${dollars.toFixed(0)}`;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - Date.parse(iso);
  if (Number.isNaN(diff)) return '';
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export function PricingPredictionCard({ lotId }: { lotId: string }) {
  const { userId } = useSession();
  const [state, setState] = useState<FetchState>({ kind: 'idle' });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const base = ieBaseUrl();
    if (!base) {
      setState({
        kind: 'error',
        status: 0,
        message: 'AuctiSync AI is not configured for this environment.',
      });
      return;
    }
    if (userId === null) {
      setState({ kind: 'error', status: 0, message: 'Sign in required.' });
      return;
    }
    const isRefresh = refreshNonce > 0;
    setState((prev) => {
      if (isRefresh && prev.kind === 'ok') {
        return { kind: 'refreshing', prior: prev.data };
      }
      return { kind: 'loading' };
    });
    const url = isRefresh
      ? `${base}/v1/pricing/refresh?lot_id=${encodeURIComponent(lotId)}`
      : `${base}/v1/pricing/lot/${encodeURIComponent(lotId)}`;
    const method = isRefresh ? 'POST' : 'GET';
    fetchWithAuth(url, {
      method,
    })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          setState({
            kind: 'error',
            status: res.status,
            message: friendlyErrorMessage(res.status, body?.detail),
          });
          return;
        }
        const data = (await res.json()) as ApiPrediction;
        setState({ kind: 'ok', data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'unknown error';
        setState({ kind: 'error', status: 0, message });
      });
    return () => {
      cancelled = true;
    };
  }, [lotId, refreshNonce, userId]);

  const refresh = (): void => {
    setRefreshNonce((n) => n + 1);
  };

  return (
    <Card>
      <CardHeader>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '0.5rem',
            flexWrap: 'wrap',
          }}
        >
          <CardTitle>AuctiSync AI hammer prediction</CardTitle>
          <button
            type="button"
            onClick={refresh}
            disabled={state.kind === 'loading' || state.kind === 'refreshing'}
            style={refreshButtonStyle(state.kind)}
          >
            {state.kind === 'refreshing' ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <CardDescription>
          Predicted hammer band + likelihood of selling, based on your auction history.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <PredictionBody state={state} />
      </CardContent>
    </Card>
  );
}

function refreshButtonStyle(kind: FetchState['kind']): React.CSSProperties {
  return {
    padding: '0.35rem 0.8rem',
    borderRadius: '0.375rem',
    border: '1px solid var(--border)',
    background: 'var(--surface-elev)',
    color: 'var(--text)',
    fontWeight: 600,
    fontSize: '0.8rem',
    cursor: kind === 'loading' || kind === 'refreshing' ? 'wait' : 'pointer',
  };
}

function PredictionBody({ state }: { state: FetchState }) {
  if (state.kind === 'idle' || state.kind === 'loading') {
    return (
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0 }}>
        Generating prediction…
      </p>
    );
  }
  if (state.kind === 'error') {
    return (
      <div style={{ fontSize: '0.85rem', color: 'var(--semantic-danger)', margin: 0 }}>
        {state.message}
      </div>
    );
  }
  const data = state.kind === 'refreshing' ? state.prior : state.data;
  const isRefreshing = state.kind === 'refreshing';
  return (
    <div style={{ display: 'grid', gap: '1rem', opacity: isRefreshing ? 0.6 : 1 }}>
      <BandRow band={data.band} />
      <SellThroughRow probability={data.band.sellThroughProbability} />
      <ConfidenceRow confidence={data.band.modelConfidence} />
      <RationaleBlock rationale={data.rationale} />
      <Footer generatedAtIso={data.generatedAt} compsCount={data.provenanceCompLotIds.length} />
    </div>
  );
}

function BandRow({ band }: { band: ConfidenceBand }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
        <BandPoint label="Low" amount={band.hammerLowCents} muted />
        <BandSeparator />
        <BandPoint label="Likely" amount={band.hammerMidCents} highlighted />
        <BandSeparator />
        <BandPoint label="High" amount={band.hammerHighCents} muted />
      </div>
    </div>
  );
}

function BandPoint({
  label,
  amount,
  muted,
  highlighted,
}: {
  label: string;
  amount: number;
  muted?: boolean;
  highlighted?: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: '0.15rem', textAlign: 'center' }}>
      <span
        style={{
          fontFamily: 'var(--font-heading-active)',
          fontWeight: highlighted ? 700 : 600,
          fontSize: highlighted ? '1.5rem' : '1.1rem',
          color: highlighted ? 'var(--brand-primary)' : muted ? 'var(--text-muted)' : 'var(--text)',
        }}
      >
        {formatCents(amount)}
      </span>
      <span
        style={{
          fontSize: '0.7rem',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </span>
    </div>
  );
}

function BandSeparator() {
  return (
    <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }} aria-hidden="true">
      ──
    </span>
  );
}

function SellThroughRow({ probability }: { probability: number }) {
  const pct = Math.round(probability * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Sell-through:</span>
      <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{pct}%</span>
    </div>
  );
}

function ConfidenceRow({ confidence }: { confidence: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Confidence:</span>
      <PartialStarRating value={confidence} max={5} />
    </div>
  );
}

/**
 * Star rating that supports partial fills via a CSS overlay technique.
 * - Bottom row: 5 outline stars, grey.
 * - Top row: 5 filled stars, gold, clipped to `width: ${value*100}%`.
 *
 * Partial stars render at any precision (e.g., 0.55 = 2.75 stars,
 * showing 2 full + 0.75 of the third).
 */
function PartialStarRating({ value, max }: { value: number; max: number }) {
  const clamped = Math.max(0, Math.min(1, value));
  const stars = '★'.repeat(max);
  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        fontSize: '1.1rem',
        lineHeight: 1,
      }}
      aria-label={`${(clamped * max).toFixed(1)} out of ${max} stars`}
      title={`${(clamped * max).toFixed(1)} / ${max}`}
    >
      <span style={{ color: 'var(--border)' }}>{stars}</span>
      <span
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${clamped * 100}%`,
          overflow: 'hidden',
          color: 'var(--brand-primary, #d4af37)',
          whiteSpace: 'nowrap',
        }}
      >
        {stars}
      </span>
    </span>
  );
}

function RationaleBlock({ rationale }: { rationale: string }) {
  return (
    <div
      style={{
        padding: '0.75rem',
        background: 'var(--surface-muted, var(--surface-elev))',
        border: '1px solid var(--border-subtle, var(--border))',
        borderRadius: '0.4rem',
        fontSize: '0.85rem',
        color: 'var(--text)',
        lineHeight: 1.5,
      }}
    >
      {rationale}
    </div>
  );
}

function Footer({ generatedAtIso, compsCount }: { generatedAtIso: string; compsCount: number }) {
  const rel = formatRelativeTime(generatedAtIso);
  return (
    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
      Generated by AuctiSync AI{rel ? ` · ${rel}` : ''} ·{' '}
      {compsCount === 0
        ? 'no comparable sales yet'
        : `${compsCount} comparable sale${compsCount === 1 ? '' : 's'}`}
    </div>
  );
}

function friendlyErrorMessage(status: number, detail: string | undefined): string {
  if (status === 503) {
    return 'AuctiSync AI is temporarily unavailable. Try Refresh in a few minutes.';
  }
  if (status === 502) {
    return 'Could not generate a prediction right now. Click Refresh to try again.';
  }
  if (status === 404) {
    return 'This lot does not have a prediction available.';
  }
  if (status === 403) {
    return 'You do not have access to this prediction.';
  }
  return detail ?? `Error: HTTP ${status || 'no-network'}`;
}
